/**
 * Customer Form Components
 * Reusable form validation and handling for login, register, and password recovery
 * 密码规则：长度必须为 8 到 20 个字符，并且包含字母、数字和至少一个特殊字符。
 */

class CustomerFormValidator extends HTMLElement {
  constructor() {
    super()
    this.emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    this.passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,20}$/i
    this.commonEmailDomains = [
      'gmail.com',
      'hotmail.com',
      'yahoo.com',
      'outlook.com',
      'telefonica.net',
      'msn.com',
      'icloud.com',
      'live.com'
    ]
    this.errorMessages = {
      NO_EMAIL: 'Bitte gib deine E-Mail-Adresse ein.',
      INVALID_EMAIL: 'Please enter a valid email address.',
      NO_PASSWORD: 'Bitte gib dein Passwort ein.',
      PASSWORD_MISMATCH: 'Die beiden eingegebenen Passwörter stimmen nicht überein.',
      REQUIRED: 'This field is required.',
      INVALID_PASSWORD:
        'Das Passwort muss 8-20 Zeichen lang sein und eine Kombination aus Buchstaben, Zahlen und/oder Sonderzeichen enthalten (darf nicht nur aus Zahlen bestehen).',
      NO_FIRST_NAME: 'Bitte gib deinen Vornamen ein.',
      NO_LAST_NAME: 'Bitte gib deinen Nachnamen ein.'
    }
  }

  connectedCallback() {
    this.init()
  }

  init() {
    this.form = this.querySelector('form')
    if (!this.form) {
      return
    }

    this.submitButton = this.form.querySelector('button[type="submit"]')
    this.serverErrorElement = this.form.querySelector('[data-server-error]')

    this.initializeFields()
    this.attachEventListeners()
    this.setupEmailAutocomplete()
    this.updateSubmitButtonState()
  }

  initializeFields() {
    this.fields = []

    // Find all validated inputs
    const inputs = this.form.querySelectorAll('[data-validate]')

    inputs.forEach(input => {
      const wrapper = input.closest('[data-input-wrapper]')
      const errorElement = wrapper?.parentElement?.querySelector('[data-error-message]')

      this.fields.push({
        input,
        wrapper,
        errorElement,
        validationType: input.getAttribute('data-validate'),
        required: input.hasAttribute('required') || input.hasAttribute('data-required')
      })
    })
  }

  attachEventListeners() {
    // Remove server errors on focus
    this.fields.forEach(({ input }) => {
      input.addEventListener('focus', () => {
        this.hideServerError()
      })
    })

    // Validate on blur
    this.fields.forEach(field => {
      field.input.addEventListener('blur', () => {
        this.validateField(field)
      })
    })

    // Update button state on input
    this.fields.forEach(field => {
      field.input.addEventListener('input', () => {
        // Clear error if input is being corrected
        if (field.wrapper?.classList.contains('error')) {
          const error = this.getFieldError(field)
          if (!error) {
            this.clearFieldError(field)
          }
        }
        this.updateSubmitButtonState()
      })
    })

    // Form submission
    this.form.addEventListener('submit', e => this.handleSubmit(e))
  }

  validateField(field) {
    const error = this.getFieldError(field)

    if (error) {
      this.showFieldError(field, error)
    } else {
      this.clearFieldError(field)
    }

    this.updateSubmitButtonState()
    return !error
  }

  getFieldError(field) {
    const { value } = field.input
    const type = field.validationType

    // Type-specific validation
    switch (type) {
      case 'email':
        if (!value || value.trim() === '') {
          return this.errorMessages.NO_EMAIL
        }
        if (!this.emailRegex.test(value)) {
          return this.errorMessages.INVALID_EMAIL
        }
        break

      case 'password':
        if (!value || value.trim() === '') {
          return this.errorMessages.NO_PASSWORD
        }
        break

      case 'register-password':
        if (!value || value.trim() === '') {
          return this.errorMessages.NO_PASSWORD
        }
        if (!this.passwordRegex.test(value)) {
          return this.errorMessages.INVALID_PASSWORD
        }
        break

      case 'reset-password': {
        if (!value || value.trim() === '') {
          return this.errorMessages.NO_PASSWORD
        }
        if (!this.passwordRegex.test(value)) {
          return this.errorMessages.INVALID_PASSWORD
        }
        const passwordField = this.fields.find(f => f.validationType === 'reset-password-confirm')
        if (passwordField && passwordField.input.value && value !== passwordField.input.value) {
          return this.errorMessages.PASSWORD_MISMATCH
        }
        break
      }

      case 'reset-password-confirm': {
        if (!value || value.trim() === '') {
          return this.errorMessages.NO_PASSWORD
        }
        const passwordField = this.fields.find(f => f.validationType === 'reset-password')
        if (passwordField && value !== passwordField.input.value) {
          return this.errorMessages.PASSWORD_MISMATCH
        }
        break
      }

      case 'first-name':
        if (!value || value.trim() === '') {
          return this.errorMessages.NO_FIRST_NAME
        }
        break

      case 'last-name':
        if (!value || value.trim() === '') {
          return this.errorMessages.NO_LAST_NAME
        }
        break

      case 'text':
        if (field.required && (!value || value.trim() === '')) {
          return this.errorMessages.REQUIRED
        }
        break
    }

    return ''
  }

  showFieldError(field, message) {
    if (field.wrapper) {
      field.wrapper.classList.add('error')
    }

    if (field.errorElement) {
      field.errorElement.textContent = message
      field.errorElement.classList.remove('hidden')
    }
  }

  clearFieldError(field) {
    if (field.wrapper) {
      field.wrapper.classList.remove('error')
    }
    if (field.errorElement) {
      field.errorElement.textContent = ''
      field.errorElement.classList.add('hidden')
    }
  }

  hideServerError() {
    if (this.serverErrorElement) {
      this.serverErrorElement.style.display = 'none'
    }
  }

  clearAllErrors() {
    this.fields.forEach(field => this.clearFieldError(field))
    this.hideServerError()
  }

  updateSubmitButtonState() {
    if (!this.submitButton) return

    const hasErrors = this.fields.some(field => {
      const error = this.getFieldError(field)
      return error !== ''
    })

    this.submitButton.disabled = hasErrors
  }

  handleSubmit(e) {
    // Validate all fields
    let hasErrors = false

    this.fields.forEach(field => {
      if (!this.validateField(field)) {
        hasErrors = true
      }
    })

    if (hasErrors) {
      e.preventDefault()
      return false
    }

    // Add loading state to submit button
    if (this.submitButton) {
      this.submitButton.disabled = true
      this.submitButton.classList.add('loading')

      // Show spinner and loading text via CSS
      const textSpan = this.submitButton.querySelector('[data-button-text]')
      const spinner = this.submitButton.querySelector('.spinner')

      if (textSpan && spinner) {
        // Hide original text, show spinner
        textSpan.classList.add('hidden')
        spinner.classList.remove('hidden')
      }

      // 添加埋点
      const sendEvent = this.submitButton.getAttribute('data-send-event')
      if (sendEvent) {
        window.WDSendEvent(sendEvent)
      }
    }

    // Default: allow normal form submission
    return true
  }

  // Set up email autocomplete for email inputs in this form
  setupEmailAutocomplete() {
    if (!this.form) return

    const emailInput = this.form.querySelector('input[type="email"]')
    if (!emailInput) return

    const suggestionsList = this.form.querySelector('.customer-email-tip-list')
    if (!suggestionsList) return

    let selectedIndex = -1
    let currentSuggestions = []
    let isSelectingEmail = false // Flag to prevent showing suggestions after selection

    // Core logic: listen to input
    emailInput.addEventListener('input', e => {
      // Don't show suggestions if we just selected an email
      if (isSelectingEmail) {
        isSelectingEmail = false
        return
      }

      const value = e.target.value.trim()

      // Show suggestions if there's any input
      if (value.length > 0) {
        const atIndex = value.indexOf('@')
        let prefix, suffix

        if (atIndex !== -1) {
          // @ already entered, filter by suffix
          prefix = value.slice(0, atIndex)
          suffix = value.slice(atIndex + 1)

          // If email is already complete (has domain), don't show suggestions
          if (suffix.length > 0 && !suffix.includes(' ')) {
            const hasCompleteDomain = this.commonEmailDomains.some(
              d => suffix.toLowerCase() === d || suffix.toLowerCase().startsWith(`${d}.`)
            )
            if (hasCompleteDomain && suffix.includes('.')) {
              hideSuggestions()
              return
            }
          }

          currentSuggestions = this.commonEmailDomains
            .filter(d => d.startsWith(suffix.toLowerCase()))
            .map(d => `${prefix}@${d}`)
        } else {
          // No @ entered, show all domain suggestions
          prefix = value
          currentSuggestions = this.commonEmailDomains.map(d => `${prefix}@${d}`)
        }

        renderSuggestions(currentSuggestions)
      } else {
        hideSuggestions()
      }
    })

    // Render suggestions list
    function renderSuggestions(list) {
      if (list.length === 0) {
        hideSuggestions()
        return
      }

      suggestionsList.innerHTML = ''
      list.forEach((item, index) => {
        const li = document.createElement('li')
        li.className = 'customer-email-tip-list-item'
        li.textContent = item
        // Use onmousedown to prevent input losing focus
        li.onmousedown = e => {
          e.preventDefault()
          selectEmail(item)
        }
        // Sync index on mouse enter
        li.onmouseenter = () => setSelectedIndex(index)
        suggestionsList.appendChild(li)
      })

      suggestionsList.classList.add('active')
      // Default select first item
      if (selectedIndex === -1) setSelectedIndex(0)
    }

    function setSelectedIndex(index) {
      const items = suggestionsList.querySelectorAll('.customer-email-tip-list-item')
      if (items.length === 0) return

      // Circular index
      let adjustedIndex = index
      if (adjustedIndex >= items.length) adjustedIndex = 0
      if (adjustedIndex < 0) adjustedIndex = items.length - 1

      selectedIndex = adjustedIndex
      items.forEach((item, i) => {
        item.classList.toggle('selected', i === adjustedIndex)
      })
    }

    function selectEmail(value) {
      isSelectingEmail = true // Set flag to prevent showing suggestions
      emailInput.value = value
      hideSuggestions()
      emailInput.focus()
      // Trigger input event to update form validation
      emailInput.dispatchEvent(new Event('input', { bubbles: true }))
    }

    function hideSuggestions() {
      suggestionsList.classList.remove('active')
      selectedIndex = -1
      currentSuggestions = []
    }

    // Keyboard navigation
    emailInput.addEventListener('keydown', e => {
      if (!suggestionsList.classList.contains('active')) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(selectedIndex + 1)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(selectedIndex - 1)
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (selectedIndex >= 0 && currentSuggestions[selectedIndex]) {
          e.preventDefault()
          selectEmail(currentSuggestions[selectedIndex])
        }
      } else if (e.key === 'Escape') {
        hideSuggestions()
      }
    })
  }
}

// Password Toggle Component
class PasswordToggle extends HTMLElement {
  connectedCallback() {
    this.input = document.getElementById(this.getAttribute('for'))
    if (!this.input) return

    this.visible = false
    this.addEventListener('click', () => this.toggle())
  }

  toggle() {
    this.visible = !this.visible
    this.input.type = this.visible ? 'text' : 'password'

    const showIcon = this.querySelector('[data-show]')
    const hideIcon = this.querySelector('[data-hide]')

    if (showIcon && hideIcon) {
      if (this.visible) {
        showIcon.classList.add('hidden')
        hideIcon.classList.remove('hidden')
      } else {
        showIcon.classList.remove('hidden')
        hideIcon.classList.add('hidden')
      }
    }
  }
}

// Register custom elements
if (!customElements.get('customer-form-validator')) {
  customElements.define('customer-form-validator', CustomerFormValidator)
}
if (!customElements.get('password-toggle')) {
  customElements.define('password-toggle', PasswordToggle)
}
